import {css} from '@emotion/react';
import {Divider, Grid, Paper, Stack, Text} from '@mantine/core';
import {DateTime} from 'luxon';
import React, {FC} from 'react';
import {useTypedParams} from 'react-router-typesafe-routes/dom';
import {AsyncStateContainer} from '../../components/async-state-container';
import {BasicPage} from '../../components/basic-page';
import {DateFormat} from '../../components/date';
import {ROUTES} from '../../routes';
import {useCertificateStore} from './job-certificate-store';
import {AnchorIcon} from '../../components/anchor-icon.tsx';
import {useAsync} from 'react-use';
import {useJobStore} from "../job-store.tsx";

type JobCertificateProps = {
  children?: React.ReactNode;
};

export const JobCertificate: React.FC<JobCertificateProps> = () => {
  const {id} = useTypedParams(ROUTES.ADMIN.JOBS.DETAIL);

  const {certificate, loading, fetchCertificate} = useCertificateStore(
    (state) => ({
      certificate: state.certificate ?? undefined,
      loading: state.loading,
      fetchCertificate: state.fetchCertificate,
    }),
  );

  useAsync(async () => {
    await fetchCertificate(id);
  }, [fetchCertificate, id]);

  return (
    <BasicPage p={0} pb={32}>
      <AsyncStateContainer loading={loading}>
        <Paper withBorder shadow='sm' m='xl' ml='15%' mr='15%'>
          <Grid bd='2px solid black'>
            <Grid.Col span={3} css={leftColCss} p={0} bg='white'>
              <ColStack>
                <TextLabel>Job ID</TextLabel>
                <RowDivider />
                <TextLabel>Serial Number</TextLabel>
                <RowDivider />
                <TextLabel>Part Number</TextLabel>
                <RowDivider />
                <TextLabel>Level of Service</TextLabel>
                <RowDivider />
                <TextLabel>Certification Created</TextLabel>
                <RowDivider />
                <TextLabel>Certification Document</TextLabel>
              </ColStack>
            </Grid.Col>
            <Grid.Col span={9} css={rightColCss} p={0} bg='white'>
              <ColStack>
                <TextValue>{certificate?.jobId}</TextValue>
                <RowDivider />
                <TextValue>{certificate?.jobSerialNumber}</TextValue>
                <RowDivider />
                <TextValue>{certificate?.jobPartNumber}</TextValue>
                <RowDivider />
                <TextValue>
                  {certificate?.jobLevelOfServiceDescription}
                </TextValue>
                <RowDivider />
                <TextValue>
                  {certificate?.createdDate && (
                    <DateFormat
                      lh='24px'
                      date={certificate.createdDate}
                      format={DateTime.DATE_SHORT}
                    />
                  )}
                </TextValue>
                <RowDivider />
                <TextValue>
                  <AnchorIcon
                    href={certificate?.url}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {certificate?.name}
                  </AnchorIcon>
                </TextValue>
              </ColStack>
            </Grid.Col>
          </Grid>
        </Paper>
      </AsyncStateContainer>
    </BasicPage>
  );
};

export const NonMmJobClosedArchived = () => {
  const {id} = useTypedParams(ROUTES.ADMIN.JOBS.DETAIL);
  
  const {
    job,
    isLoading,
  } = useJobStore((state) => ({
    job: state.job,
    isLoading: state.isJobLoading,
  }));

  const {certificate, loading, fetchCertificate} = useCertificateStore(
    (state) => ({
      certificate: state.certificate ?? undefined,
      loading: state.loading,
      fetchCertificate: state.fetchCertificate,
    }),
  );

  useAsync(async () => {
    await fetchCertificate(id);
  }, [fetchCertificate, id]);
  
  return (
    <BasicPage p={0} pb={32}>
      <AsyncStateContainer loading={isLoading || loading}>
        <Paper withBorder shadow='sm' m='xl' ml='15%' mr='15%'>
          <Grid bd='2px solid black'>
            <Grid.Col span={3} css={leftColCss} p={0} bg='white'>
              <ColStack>
                <TextLabel>Job ID</TextLabel>
                <RowDivider />
                <TextLabel>Serial Number</TextLabel>
                <RowDivider />
                <TextLabel>Part Number</TextLabel>
                <RowDivider />
                <TextLabel>Job Closed Date</TextLabel>
                <RowDivider />
                <TextLabel>Repair Summary</TextLabel>
              </ColStack>
            </Grid.Col>
            <Grid.Col span={9} css={rightColCss} p={0} bg='white'>
              <ColStack>
                <TextValue>{job?.id}</TextValue>
                <RowDivider />
                <TextValue>{job?.serialNumber}</TextValue>
                <RowDivider />
                <TextValue>{job?.partNumber}</TextValue>
                <RowDivider />
                <TextValue>
                  {job?.dateClosed &&
                    <DateFormat
                      lh='24px'
                      date={job?.dateClosed}
                      format={DateTime.DATE_SHORT}
                    />
                  }
                </TextValue>
                <RowDivider />
                <TextValue>
                  <AnchorIcon
                    href={certificate?.url}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {certificate?.name}
                  </AnchorIcon>
                </TextValue>
              </ColStack>
            </Grid.Col>
          </Grid>
        </Paper>
      </AsyncStateContainer>
    </BasicPage>
  );
}

type Parent = {
  children?: React.ReactNode;
};

const TextLabel: FC<Parent> = ({children}) => (
  <Text ta='center' h={44} fw='bold' fz={14} p='xs' m='auto auto auto 12'>
    {children}
  </Text>
);

const TextValue: FC<Parent> = ({children}) => (
  <Text ta='center' h={44} fz={14} p='xs' m='auto auto auto 12'>
    {children}
  </Text>
);

const RowDivider: FC = () => <Divider size={2} color='rgba(0, 0, 0, 0.05)' />;

const ColStack: FC<Parent> = ({children}) => (
  <Stack justify='center' gap={0}>
    {children}
  </Stack>
);

const leftColCss = css({
  border: '1px solid rgba(0, 0, 0, 0.15)',
  borderRight: '0.5px solid rgba(0, 0, 0, 0.075)',
  borderRadius: '6px 0 0 6px',
});

const rightColCss = css({
  border: '1px solid rgba(0, 0, 0, 0.15)',
  borderLeft: '0.5px solid rgba(0, 0, 0, 0.075)',
  borderRadius: '0 6px 6px 0',
});